import React, { useEffect, useLayoutEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import "@fontsource/montserrat";
import { SidebarData } from "./SidebarData";
import { IconContext } from "react-icons/lib";
import "semantic-ui-css/semantic.min.css";
import { useAuthProvider } from "../../Providers/AuthProvider";
import { Deployment, useEnvironmentVariablesProvider } from "../../Providers/EnvironmentVariablesProvider"
import { MobergFontFamily } from "../MobergFont/MobergFont";
import { Link } from "react-router-dom";
import * as MdIcons from 'react-icons/md';
import { useModalProvider } from '../../Providers/ModalProvider';
import { useUploadIntoQueueProvider } from '../../Providers/UploadIntoQueueProvider';
import { useUploadQueueProvider } from '../../Providers/UploadQueueProvider';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { FRONTEND_LINKS } from '../../Constants/FrontendLinks';
import { MobergTheme } from '../MobergThemes/MobergColors';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { SOFTWARE_VERSION } from '../../Version';
import { MdDarkMode, MdLightMode } from 'react-icons/md';
import { useMobergTheme } from '../../Providers/MobergThemeProvider';

const Sidebar = (props) => {
	const authProvider = useAuthProvider()
	const { deployment, environmentVariables } = useEnvironmentVariablesProvider()
	const { colorScheme, setColorScheme } = useMobergTheme()
	const isDark = colorScheme === "dark"
	const isClinical = deployment === Deployment.CLINICAL 
	
	const SIDEBAR_TABS = useMemo(() => {
		if (isClinical) {
			
			return SidebarData.map(section => {
				if (section.title === 'WORKSPACE') {
					return {
						...section,
						subNav: section.subNav.filter(item => 
							!item.sub_page && item.title !== 'Projects' && item.title !== 'Sites'
						)
					};
				}
				return section;
			});
		}
		// For non-clinical mode, filter out clinical_only items
		return SidebarData.map(section => ({
			...section,
			subNav: section.subNav.filter(item => !item.clinical_only)
		}));
	}, [isClinical])

	const [sidebarOpen, setSidebarOpen] = useState(false)
	const [linkActiveIndex, setLinkActiveIndex] = useState(1);

	const { createModal } = useModalProvider();
	const uploadIntoQueueProvider = useUploadIntoQueueProvider();
	const uploadQueueProvider = useUploadQueueProvider();
	const navigate = useNavigate();

	function useWindowSize() {
		const [size, setSize] = useState(0);
		useLayoutEffect(() => {
			function updateSize() {
				setSize(window.innerHeight);
			}
			window.addEventListener('resize', updateSize);
			updateSize();
			return () => window.removeEventListener('resize', updateSize);
		}, []);
		return size;
	}

	const size = useWindowSize()
	const location = useLocation();
	useEffect(() => {
		for (let group of SIDEBAR_TABS) {
			for (let section of group['subNav']) {
				if (section.path === location.pathname) {
					setLinkActiveIndex(section.index)
				}
			}
		}
	}, [SIDEBAR_TABS, location]);

	const [enableSideBar, setEnableSideBar] = useState(true)

	useEffect(() => {
		if (authProvider.userIsLoggedIn && location.pathname === '/inactive') {
			setEnableSideBar(false)
		} else {
			setEnableSideBar(true)
		}
		if (!authProvider.userIsLoggedIn) {
			setEnableSideBar(false)
		}
	}, [authProvider.userIsLoggedIn, location.pathname])

	
	const handleMouseEnter = () => setSidebarOpen(true)
	const handleMouseLeave = () => setSidebarOpen(false)

	async function cancelUpload() {
		if (uploadIntoQueueProvider.uploadID) {
			let body = {
				upload_id: uploadIntoQueueProvider.uploadID,
			};
			return uploadIntoQueueProvider.endpointProvider.post(uploadIntoQueueProvider.LINKS.DATA.UPLOAD.CANCEL_UPLOAD, body);
		}
	}

	function logout() {
		uploadIntoQueueProvider.setOpenQueue(false);
		uploadIntoQueueProvider.undoExistingValidation(uploadIntoQueueProvider.folderID);
		uploadQueueProvider.setUploadInProgress(false);
		if (props.setSidebar) props.setSidebar(false);
		if (props.setNavPage) props.setNavPage(false);
		authProvider.logout();
		navigate(FRONTEND_LINKS.MAIN);
	}

	const handleLogout = (e) => {
		if (uploadQueueProvider.uploadInProgress) {
			createModal(<ConfirmationModal
				title={"Remove upload?"}
				description={`Are you sure you want to log out? Logging out will cancel your current upload and remove all uploads from queue.`}
				confirmButton={{ text: "Yes, log out", theme: MobergTheme.RED, onClick: cancelUpload }}
				cancelButton={{ text: "No, continue upload" }}
				afterClose={() => logout()}
			/>)
		} else {
			logout();
		}
	}

	return (
		<div style={{ display: enableSideBar ? "block" : "none", position: 'relative', zIndex: 1200 }}>
			<IconContext.Provider value={{ color: "var(--color-accent-interactive)" }}>
				<SidebarNav
					sidebar={sidebarOpen}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					<SidebarWrap sidebar={sidebarOpen} style={{ height: 'calc(100vh - 78px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 0 }}>
						
						<div style={{ width: '100%' }}>
							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 8 }}>
								{SIDEBAR_TABS.map((section, sectionIdx) => {
									const accessibleItems = section.subNav.filter(item => {
										if (item.sub_page) return false
										if (item.feature_flag && !environmentVariables?.[item.feature_flag]) return false
										return authProvider.permissions && authProvider.permissions[item.action_id]
									});
									
									if (accessibleItems.length === 0) {
										return null;
									}
									
									return (
										<React.Fragment key={sectionIdx}>
											{accessibleItems.map((item, idx) => (
												<SidebarIconButton
													to={item.path}
													key={item.title || idx}
													active={item.index === linkActiveIndex}
													sidebaropen={sidebarOpen ? 1 : 0}
												>
													{item.icon}
													{sidebarOpen && <SidebarIconLabel>{item.title}</SidebarIconLabel>}
												</SidebarIconButton>
											))}
										</React.Fragment>
									);
								})}
								{authProvider.userIsLoggedIn && environmentVariables?.FEATURE_FLAG_SANDBOX && environmentVariables?.SANDBOX_URL && (
									<SidebarIconButtonButton
										onClick={() => window.open(environmentVariables.SANDBOX_URL, "_blank", "noopener,noreferrer")}
										active={false}
										sidebaropen={sidebarOpen ? 1 : 0}
										style={{ width: '100%' }}
										aria-label="Open Analytics Studio (opens in a new tab)"
									>
										<MdIcons.MdScience size={22} />
										{sidebarOpen && (
											<>
												<SidebarIconLabel>Analytics Studio</SidebarIconLabel>
												<MdIcons.MdOpenInNew size={18} style={{ marginLeft: 'auto', opacity: 0.7 }} />
											</>
										)}
									</SidebarIconButtonButton>
								)}
							</div>
						</div>
						
						
						{authProvider.userIsLoggedIn && (
							<div style={{ width: '100%', marginBottom: 32 }}>
								{environmentVariables?.FEATURE_FLAG_DARK_MODE && (
									<ThemeButton
										onClick={() => setColorScheme(isDark ? "light" : "dark")}
										sidebaropen={sidebarOpen ? 1 : 0}
										aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
									>
										{isDark ? <MdDarkMode size={22} /> : <MdLightMode size={22} />}
										<SidebarIconLabel style={{ opacity: sidebarOpen ? 1 : 0, width: sidebarOpen ? 'auto' : 0, marginLeft: sidebarOpen ? 18 : 0, overflow: 'hidden', whiteSpace: 'nowrap', transition: 'opacity 0.18s, width 0.18s, margin-left 0.18s' }}>{isDark ? "Light mode" : "Dark mode"}</SidebarIconLabel>
									</ThemeButton>
								)}
								<SidebarIconButtonButton
									onClick={handleLogout}
									active={false}
									sidebaropen={sidebarOpen ? 1 : 0}
									logout={true}
									style={{ width: '100%', marginBottom: 8 }}
								>
									<MdIcons.MdLogout size={22} />
									{sidebarOpen && <SidebarIconLabel>Logout</SidebarIconLabel>}
								</SidebarIconButtonButton>
								<SidebarDivider style={{  }} />
								<SidebarVersionContainer sidebaropen={sidebarOpen ? 1 : 0}>
									{sidebarOpen ? `VERSION: v${SOFTWARE_VERSION}` : `v${SOFTWARE_VERSION}`}
								</SidebarVersionContainer>
							</div>
						)}
					</SidebarWrap>
				</SidebarNav>
				
				<SidebarToggleButton
					onClick={() => setSidebarOpen((open) => !open)}
					sidebaropen={sidebarOpen ? 1 : 0}
					aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
					style={{
						position: 'absolute',
						top: 'calc(50vh + 35px)',
						left: sidebarOpen ? 200 + 16 - 20 : 98 + 16 - 30,
						transform: 'translateY(-50%)',
						zIndex: 2000,
					}}
				>
					{sidebarOpen ? <MdChevronLeft size={22} /> : <MdChevronRight size={22} />}
				</SidebarToggleButton>
			</IconContext.Provider>
		</div>
	)
}

export default Sidebar;

const SidebarNav = styled.nav`

    width: ${({ sidebar }) => (sidebar ? '200px' : '98px')};
    height: calc(100vh - 60px);
    display: flex;
	background: var(--color-surface-panel);
    justify-content: center;
    position: fixed;
    top: 60px;
    overflow-y: hidden;
    overflow-x: visible;
	border-radius-bottom-left: 4px;
	border-right: 1px solid var(--color-border-light);
    z-index: 1000;
    transition: width 0.18s cubic-bezier(.4,0,.2,1);
`;

const SidebarWrap = styled.div`
	width: 100%;
	transition: background 0.18s cubic-bezier(.4,0,.2,1);
`;

const SidebarIconButton = styled(Link)`
	display: flex;
	align-items: center;
	justify-content: ${({ sidebaropen }) => (sidebaropen ? 'flex-start' : 'center')};
	width: 100%;
	height: 56px;
	margin: 0 auto 4px auto;
	padding: ${({ sidebaropen }) => (sidebaropen ? '0 24px' : '0')};
	border-radius: 0;
	color: ${({ active }) => (active ? 'var(--color-accent-interactive)' : 'var(--text-secondary-color)')};
	background: transparent;
	font-size: 22px;
	text-decoration: none;
	transition: background 0.15s, color 0.15s, padding 0.18s cubic-bezier(.4,0,.2,1);
	&:hover {
		background: var(--color-hover-neutral);
		color: var(--color-accent-interactive);
	}
	& > svg {
		color: inherit !important;
	}
`;

const SidebarIconButtonButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: ${({ sidebaropen }) => (sidebaropen ? 'flex-start' : 'center')};
	width: 100%;
	height: 56px;
	margin: 0 auto 4px auto;
	padding: ${({ sidebaropen }) => (sidebaropen ? '0 24px' : '0')};
	border-radius: 0;
	color: ${({ active, logout }) => logout ? 'var(--color-danger-text)' : (active ? 'var(--color-accent-interactive)' : 'var(--text-secondary-color)')};
	background: ${({ active, logout }) => {
		if (logout) return 'transparent';
		return active ? 'var(--color-hover-active-blue)' : 'transparent';
	}};
	font-size: 22px;
	text-decoration: none;
	transition: background 0.15s, color 0.15s, padding 0.18s cubic-bezier(.4,0,.2,1);
	border: none;
	cursor: pointer;
	&:hover {
		background: ${({ logout }) => logout ? 'var(--color-danger-hover-bg)' : 'var(--color-hover-neutral)'};
		color: ${({ logout }) => logout ? 'var(--color-danger-text-hover)' : 'var(--color-accent-interactive)'};
	}
	& > svg {
		color: inherit !important;
	}
`;

const SidebarIconLabel = styled.span`
	margin-left: 18px;
	font-size: 16px;
	font-family: ${MobergFontFamily.REGULAR};
	font-weight: 600;
	color: inherit;
	letter-spacing: 0.04em;
	text-align: left;
`;

const ThemeButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: ${({ sidebaropen }) => (sidebaropen ? 'flex-start' : 'center')};
	width: 100%;
	height: 56px;
	margin: 0 auto 4px auto;
	padding: ${({ sidebaropen }) => (sidebaropen ? '0 24px' : '0')};
	border-radius: 0;
	color: var(--text-secondary-color);
	background: transparent;
	font-size: 22px;
	text-decoration: none;
	border: none;
	cursor: pointer;
	transition: background 0.15s, color 0.15s, padding 0.18s cubic-bezier(.4,0,.2,1);
	&:hover {
		background: var(--color-hover-neutral);
		color: var(--color-accent-interactive);
	}
	& > svg {
		flex-shrink: 0;
		color: inherit !important;
	}
`;

const SidebarDivider = styled.div`
	width: 90%;
	height: 1px;
	background: var(--color-border-light);
	margin: 16px auto 16px auto;
`;

const SidebarVersionContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2px 16px;
	font-size: 12px;
	font-family: ${MobergFontFamily.REGULAR};
	font-weight: 600;
	color: var(--color-text-version);
	background: var(--color-hover-neutral);
	border-radius: 12px;
	letter-spacing: 0.08em;
	box-shadow: 0 1px 2px rgba(60, 60, 60, 0.04);
	border: 1px solid var(--color-border-light);
	margin: 8px auto;
	transition: all 0.18s cubic-bezier(.4,0,.2,1);
	white-space: nowrap;
	margin-left: 16px;
	margin-right: 16px;
	overflow: hidden;
	text-overflow: ellipsis;
`;


const SidebarToggleButton = styled.button`
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: var(--color-surface-panel);
	box-shadow: 0 4px 16px rgba(60, 60, 60, 0.12);
	border: 1px solid var(--color-border-light);
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: var(--text-secondary-color);
	transition: box-shadow 0.18s cubic-bezier(.4,0,.2,1);
	&:hover {
		box-shadow: 0 6px 24px rgba(60, 60, 60, 0.18);
	}
`;